import React, { useState } from 'react';
import { getFullApiUrl } from '../config';
import { addAuthHeader } from '../utils/auth';

const FinancialInsightsCard = ({ analysisResults, aiInsights, onInsightsGenerated, isDataLoaded, analysisId }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  // Get insights provider from localStorage
  const llmConfig = JSON.parse(localStorage.getItem('llmConfig') || '{}');
  const insightsProvider = llmConfig.insightsProvider || 'gemini';

  const generateInsights = async () => {
    if (!analysisResults) return;

    setIsGenerating(true);
    try {
      // Build analysis data from current results
      const analysisData = analysisResults.complete_analysis || [];

      // Get LLM configuration from localStorage
      const llmConfig = JSON.parse(localStorage.getItem('llmConfig') || '{}');
      const insightsProvider = llmConfig.insightsProvider || 'gemini';
      const insightsModel = llmConfig[`${insightsProvider}Model`] || (insightsProvider === 'groq' ? 'llama-3.3-70b-versatile' : 'gemini-2.5-pro');
      const providerApiKey = insightsProvider === 'groq' ? llmConfig.groqApiKey : llmConfig.geminiApiKey;

      const response = await fetch(getFullApiUrl('FINANCIAL_INSIGHTS'), {
        method: 'POST',
        headers: addAuthHeader({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          analysis_data: analysisData,
          provider: insightsProvider,
          model: insightsModel,
          api_key: providerApiKey || undefined,
          analysis_id: analysisId || undefined  // Kirim analysis_id agar disimpan ke DB
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        if (onInsightsGenerated) {
          onInsightsGenerated(result.insights);
        }
      } else {
        console.error('Failed to generate insights:', result.message);
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isDataLoaded || !analysisResults) {
    return null;
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'EXCELLENT': return 'bg-green-100 text-green-800';
      case 'GOOD': return 'bg-blue-100 text-blue-800';
      case 'MODERATE': return 'bg-yellow-100 text-yellow-800';
      case 'WEAK': return 'bg-orange-100 text-orange-800';
      case 'POOR': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-100 text-red-700 border-red-300';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'LOW': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <section className="mb-8">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-indigo-400">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-white flex items-center">
                <i className="fa-solid fa-lightbulb mr-2"></i>
                AI Financial Health Insights
              </h3>
            </div>
            {!aiInsights && (
              <button
                onClick={generateInsights}
                disabled={isGenerating}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${isGenerating
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-white text-indigo-600 hover:bg-indigo-50'
                  }`}
              >
                <i className={`fa-solid ${isGenerating ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'} mr-2`}></i>
                {isGenerating ? 'Generating...' : 'Generate Insights'}
              </button>
            )}
          </div>
        </div>

        {!aiInsights && !isGenerating && (
          <div className="px-6 py-8 text-center">
            <i className="fa-solid fa-brain text-5xl text-white opacity-50 mb-4"></i>
            <p className="text-white text-sm">
              Klik "Generate Insights" untuk mendapatkan analisis mendalam dari AI
            </p>
          </div>
        )}

        {isGenerating && (
          <div className="px-6 py-8 text-center">
            <i className="fa-solid fa-brain text-5xl text-white animate-pulse mb-4"></i>
            <p className="text-white text-sm">
              AI sedang menganalisis data keuangan Anda...
            </p>
          </div>
        )}

        {aiInsights && (
          <div className="bg-white p-6">
            {/* Overall Assessment */}
            <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-800">
                  Overall Assessment
                </h4>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(aiInsights.overall_assessment?.category)}`}>
                    {aiInsights.overall_assessment?.category}
                  </span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {aiInsights.overall_assessment?.health_score}
                  </span>
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                {aiInsights.overall_assessment?.executive_summary}
              </p>
            </div>

            {/* Key Strengths */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                <i className="fa-solid fa-check-circle text-green-500 mr-2"></i>
                Kekuatan Utama
              </h4>
              <ul className="space-y-2">
                {aiInsights.key_strengths?.map((strength, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-500 mr-2 mt-1">•</span>
                    <span className="text-sm text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Areas of Concern */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                <i className="fa-solid fa-exclamation-triangle text-orange-500 mr-2"></i>
                Area Perhatian
              </h4>
              <ul className="space-y-2">
                {aiInsights.areas_of_concern?.map((concern, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-orange-500 mr-2 mt-1">•</span>
                    <span className="text-sm text-gray-700">{concern}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Financial Trends */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="text-sm font-semibold text-blue-800 mb-2">Revenue Trend</h5>
                <p className="text-xs text-gray-700">{aiInsights.financial_trends?.revenue_trend}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h5 className="text-sm font-semibold text-green-800 mb-2">Profitability Trend</h5>
                <p className="text-xs text-gray-700">{aiInsights.financial_trends?.profitability_trend}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h5 className="text-sm font-semibold text-purple-800 mb-2">Working Capital Trend</h5>
                <p className="text-xs text-gray-700">{aiInsights.financial_trends?.working_capital_trend}</p>
              </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                <i className="fa-solid fa-bullseye text-indigo-600 mr-2"></i>
                Rekomendasi Strategis
              </h4>
              <div className="space-y-3">
                {aiInsights.strategic_recommendations?.map((rec, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase">{rec.priority} Priority</span>
                      <span className="text-xs">{rec.timeline}</span>
                    </div>
                    <p className="text-sm font-medium mb-1">{rec.action}</p>
                    <p className="text-xs opacity-80">{rec.expected_impact}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Factors & Opportunities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <i className="fa-solid fa-shield-halved text-red-500 mr-2"></i>
                  Faktor Risiko
                </h4>
                <ul className="space-y-2">
                  {aiInsights.risk_factors?.map((risk, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-red-500 mr-2 mt-1">•</span>
                      <span className="text-sm text-gray-700">{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                  <i className="fa-solid fa-rocket text-blue-500 mr-2"></i>
                  Peluang
                </h4>
                <ul className="space-y-2">
                  {aiInsights.opportunities?.map((opp, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-1">•</span>
                      <span className="text-sm text-gray-700">{opp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FinancialInsightsCard;

